'use client';

import { useEffect, useRef, useState } from 'react';
import { isBubbleSymbol } from '@/utils/coins/bubble';

interface CoinPriceData {
    livePriceToman: number;
    livePriceUsdt: number;
    priceTomanChangeClass: string | null;
    priceUsdtChangeClass: string | null;
    livePriceUpdatedAt?: string;
    /** 24h change in percent from the socket; undefined until the first update arrives */
    liveChange24h?: number;
    /** 24h change in toman from the socket (gold & currency only) */
    liveChangeToman?: number;
}

type PriceMessage = Record<string, any>;
type Listener = (msg: PriceMessage) => void;

interface Channel {
    ws: WebSocket | null;
    listeners: Set<Listener>;
    last?: PriceMessage;
    attempts: number;
    retryTimer?: ReturnType<typeof setTimeout>;
}

// One socket per URL shared by every component showing that symbol (header, sticky bar, chart, lists)
const channels = new Map<string, Channel>();

const connect = (url: string, channel: Channel) => {
    const ws = new WebSocket(url);
    channel.ws = ws;

    ws.onopen = () => {
        channel.attempts = 0;
    };
    ws.onmessage = (evt) => {
        try {
            const msg = JSON.parse(evt.data);
            if (msg?.type !== 'price_update') return;
            channel.last = msg;
            channel.listeners.forEach((listener) => listener(msg));
        } catch (e) {
            console.error(`[WS ${url}] parse error`, e);
        }
    };
    ws.onclose = () => {
        if (channel.ws !== ws || channel.listeners.size === 0) return;
        // Server dropped us (deploy, 502, network): reconnect with backoff, max 30s
        const delay = Math.min(30000, 1000 * 2 ** channel.attempts);
        channel.attempts += 1;
        channel.retryTimer = setTimeout(() => {
            if (channels.get(url) === channel && channel.listeners.size > 0) connect(url, channel);
        }, delay);
    };
};

const subscribe = (url: string, listener: Listener): (() => void) => {
    let channel = channels.get(url);
    if (!channel) {
        channel = { ws: null, listeners: new Set(), attempts: 0 };
        channels.set(url, channel);
        connect(url, channel);
    }
    channel.listeners.add(listener);
    // Late subscribers get the latest known price right away
    const { last } = channel;
    if (last) queueMicrotask(() => listener(last));

    const current = channel;
    return () => {
        current.listeners.delete(listener);
        if (current.listeners.size > 0) return;
        clearTimeout(current.retryTimer);
        channels.delete(url);
        const ws = current.ws;
        current.ws = null;
        if (!ws) return;
        ws.onmessage = null;
        // Closing while CONNECTING makes the browser report an error, so wait for the open
        if (ws.readyState === WebSocket.CONNECTING) {
            ws.onopen = () => ws.close();
        } else if (ws.readyState === WebSocket.OPEN) {
            try {
                ws.close();
            } catch {}
        }
    };
};

const toNumber = (value: unknown): number =>
    typeof value === 'string' ? parseFloat(value) : Number(value);

export function useCoinPriceSocket(
    symbol: string,
    initialPriceUsdt: number,
    initialPriceToman: number,
    symbolType: 'crypto' | 'coin' | 'gold' | 'currency' = 'crypto',
    initialPriceUpdatedAt?: string,
): CoinPriceData {
    const [livePriceToman, setLivePriceToman] = useState<number>(initialPriceToman || 0);
    const [livePriceUsdt, setLivePriceUsdt] = useState<number>(initialPriceUsdt || 0);
    const [priceTomanChangeClass, setPriceTomanChangeClass] = useState<string | null>(null);
    const [priceUsdtChangeClass, setPriceUsdtChangeClass] = useState<string | null>(null);
    const [livePriceUpdatedAt, setLivePriceUpdatedAt] = useState<string | undefined>(
        initialPriceUpdatedAt,
    );
    const [liveChange24h, setLiveChange24h] = useState<number | undefined>(undefined);
    const [liveChangeToman, setLiveChangeToman] = useState<number | undefined>(undefined);

    const prevTomanRef = useRef<number | null>(null);
    const prevUsdtRef = useRef<number | null>(null);

    useEffect(() => {
        if (!symbol) return;

        // Reset state when symbol changes
        setLivePriceToman(initialPriceToman || 0);
        setLivePriceUsdt(initialPriceUsdt || 0);
        setLivePriceUpdatedAt(initialPriceUpdatedAt);
        setLiveChange24h(undefined);
        setLiveChangeToman(undefined);
        prevTomanRef.current = initialPriceToman || 0;
        prevUsdtRef.current = initialPriceUsdt || 0;

        const isCrypto =
            symbol.toUpperCase() === 'USDT' ||
            symbolType === 'crypto' ||
            symbolType === 'coin' ||
            (symbolType !== 'gold' && symbolType !== 'currency');
        const endpointCategory = isCrypto ? 'symbol' : 'currency';
        const url = `wss://market-pulse-ws.khanes.app/ws/${endpointCategory}/price/${symbol.toUpperCase()}`;
        const allowNegative = isBubbleSymbol(symbol);

        return subscribe(url, (msg) => {
            let hasUpdate = false;

            // Crypto sockets send price_toman; the gold/currency socket sends price_in_toman
            const rawToman = msg.price_toman ?? msg.price_in_toman;
            if (rawToman != null) {
                const nextToman = toNumber(rawToman);
                // Gold bubbles can legitimately be negative (coin below its gold value)
                const isValidToman = allowNegative
                    ? Number.isFinite(nextToman)
                    : Number.isFinite(nextToman) && nextToman > 0;
                if (isValidToman) {
                    const prevToman = prevTomanRef.current;
                    if (prevToman != null && prevToman !== nextToman) {
                        setPriceTomanChangeClass(
                            nextToman > prevToman ? 'text-[#13C783]' : 'text-red-500',
                        );
                    }
                    prevTomanRef.current = nextToman;
                    setLivePriceToman(nextToman);
                    hasUpdate = true;
                }
            }

            // Crypto sockets send price_usdt; the gold/currency socket sends price_usd for
            // dollar-quoted assets (the world gold ounce)
            const rawUsd = msg.price_usdt ?? msg.price_usd;
            if (rawUsd != null) {
                const nextUsdt = toNumber(rawUsd);
                if (Number.isFinite(nextUsdt) && nextUsdt > 0) {
                    const prevUsdt = prevUsdtRef.current;
                    if (prevUsdt != null && prevUsdt !== nextUsdt) {
                        setPriceUsdtChangeClass(
                            nextUsdt > prevUsdt ? 'text-[#13C783]' : 'text-red-500',
                        );
                    }
                    prevUsdtRef.current = nextUsdt;
                    setLivePriceUsdt(nextUsdt);
                    hasUpdate = true;
                }
            }

            const change = toNumber(msg.change_24h ?? msg.change_24);
            if (Number.isFinite(change)) setLiveChange24h(change);
            const changeToman = toNumber(msg.change_toman);
            if (Number.isFinite(changeToman)) setLiveChangeToman(changeToman);

            if (hasUpdate) {
                setLivePriceUpdatedAt(
                    msg.updated_at || msg.price_updated_at || new Date().toISOString(),
                );
            }
        });
    }, [symbol, initialPriceUsdt, initialPriceToman, symbolType, initialPriceUpdatedAt]);

    // Clear flash classes after delay
    useEffect(() => {
        if (!priceTomanChangeClass) return;
        const timer = setTimeout(() => setPriceTomanChangeClass(null), 1200);
        return () => clearTimeout(timer);
    }, [priceTomanChangeClass]);

    useEffect(() => {
        if (!priceUsdtChangeClass) return;
        const timer = setTimeout(() => setPriceUsdtChangeClass(null), 1200);
        return () => clearTimeout(timer);
    }, [priceUsdtChangeClass]);

    return {
        livePriceToman,
        livePriceUsdt,
        priceTomanChangeClass,
        priceUsdtChangeClass,
        livePriceUpdatedAt,
        liveChange24h,
        liveChangeToman,
    };
}
